import { useEffect, useState } from 'react';
import {
  isCurrentUserKidsAdmin,
  listAdminAuditLog,
  listAdminArticles,
  listAdminAvatars,
  listAdminBadges,
  listAdminCategories,
  listAdminFeatureFlags,
  listAdminLearningModules,
  listAdminLearningPaths,
  listAdminLessons,
  listAdminParentRequests,
  listAdminQuizReadiness,
  listAdminQuizReports,
  listAdminRewardItems,
  listMediaAssets,
  type AdminQuizReadinessRow,
  type AdminQuizReportRow,
} from './lib/kidsApi';
import type {
  KidsAvatar,
  KidsBadge,
  KidsCmsAuditLog,
  KidsFeatureFlag,
  KidsLearningArticle,
  KidsLearningModule,
  KidsLearningPath,
  KidsLesson,
  KidsMediaAsset,
  KidsParentRequest,
  KidsQuizCategory,
  KidsRewardItem,
} from './types';

export type AdminLoadState = 'loading' | 'synced' | 'local';
export type AdminSaveState = 'idle' | 'saving' | 'synced' | 'local' | 'error';

const reportRows = [
  ['Surah Names Quiz', '84%', '1,240 attempts', 'Healthy'],
  ['Al-Fatihah Meanings', '58%', '620 attempts', 'Review explanations'],
  ['Morning Dua Review', '73%', '880 attempts', 'Add hints'],
];

const moderationRows = [
  ['Parent request', 'Typo in dua explanation', 'Content correction', 'Open'],
  ['Quiz issue', 'Question too hard for age 5-6', 'Quiz review', 'In review'],
  ['Media note', 'Missing alt text on cover image', 'Accessibility', 'Open'],
];

const cmsRows = [
  ['Surah Names Quiz', 'Surahs', 'Easy', 'Published'],
  ['Al-Fatihah Meanings', 'Meanings', 'Medium', 'Draft'],
  ['Prophets in the Quran', 'Prophets', 'Medium', 'Published'],
];

const rewardCards = [
  ['First Quiz', 'Unlocked after completing your first Quran quiz.', 'Unlocked'],
  ['7 Correct Answers', 'Answer 7 questions correctly in one quiz.', 'Unlocked'],
  ['Surah Explorer', 'Explore 5 Surahs in the knowledge base.', '3 of 5'],
  ['Week Streak', 'Study 7 days in a row.', '5 of 7'],
  ['Meaning Master', 'Complete 3 meanings quizzes.', 'Locked'],
  ['Dua Review Badge', 'Finish 3 dua quizzes with review.', 'Coming soon'],
];

const adminQuizRows = [
  ['Surah Names Quiz', 'Surahs', 'Easy', 'Published', '24 questions'],
  ['Al-Fatihah Meanings', 'Meanings', 'Medium', 'Draft', '18 questions'],
  ['Morning Dua Review', 'Duas', 'Easy', 'Review', '12 questions'],
];

const articleRows = [
  ['Surah Al-Fatihah', 'Surahs', 'Published', 'Related: Surah Names Quiz'],
  ['Morning Dua Before Study', 'Daily Duas', 'Draft', 'Needs Arabic review'],
  ['What does ihdina mean?', 'Meanings', 'Review', 'Parent-friendly summary ready'],
];

export function useAdminGuard() {
  const [state, setState] = useState<'checking' | 'allowed' | 'denied'>('checking');

  useEffect(() => {
    let mounted = true;
    isCurrentUserKidsAdmin()
      .then((isAdmin) => {
        if (mounted) setState(isAdmin ? 'allowed' : 'denied');
      })
      .catch(() => {
        if (mounted) setState('denied');
      });

    return () => {
      mounted = false;
    };
  }, []);

  return { state };
}

export function toSlug(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'Unable to sync with Supabase.';
}

export function formatDateTime(value: string) {
  return new Date(value).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function humanizeAuditValue(value: unknown) {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value === null || value === undefined) return 'empty';
  return 'changed';
}

export function getAuditSummary(entry: KidsCmsAuditLog) {
  if (entry.action === 'insert') return 'Created record';
  if (entry.action === 'delete') return 'Deleted record';

  const beforeData = entry.before_data ?? {};
  const afterData = entry.after_data ?? {};
  const changedKeys = Object.keys(afterData).filter((key) => beforeData[key] !== afterData[key]);
  const displayKeys = changedKeys.filter((key) => !['updated_at', 'created_at'].includes(key)).slice(0, 3);

  if (!displayKeys.length) return 'Updated metadata';
  return displayKeys.map((key) => `${key}: ${humanizeAuditValue(beforeData[key])} -> ${humanizeAuditValue(afterData[key])}`).join(', ');
}

export function useAdminReportRows() {
  const [reports, setReports] = useState<AdminQuizReportRow[]>([]);
  const [state, setState] = useState<AdminLoadState>('loading');

  useEffect(() => {
    let isMounted = true;
    listAdminQuizReports()
      .then((items) => {
        if (!isMounted) return;
        setReports(items);
        setState('synced');
      })
      .catch(() => {
        if (!isMounted) return;
        setReports([]);
        setState('local');
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const rows = reports.length
    ? reports.map((report) => [
        report.quiz_title,
        `${report.average_accuracy}%`,
        `${report.attempts} attempt${report.attempts === 1 ? '' : 's'}`,
        report.status,
      ])
    : reportRows;

  return { reports, rows, state };
}

export function useAdminCategories() {
  const [categories, setCategories] = useState<KidsQuizCategory[]>([]);
  const [state, setState] = useState<AdminLoadState>('loading');

  useEffect(() => {
    let isMounted = true;
    listAdminCategories()
      .then((items) => {
        if (!isMounted) return;
        setCategories(items);
        setState('synced');
      })
      .catch(() => {
        if (!isMounted) return;
        setCategories([]);
        setState('local');
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return { categories, state };
}

export function useAdminCategoryRows() {
  const { categories, state } = useAdminCategories();
  const rows = categories.length
    ? categories.map((category) => [
        category.title,
        category.slug,
        category.description ?? 'No description',
        category.is_active === false ? 'Inactive' : 'Active',
      ])
    : cmsRows;

  return { categories, rows, state };
}

export function useAdminLearningPathRows() {
  const [paths, setPaths] = useState<KidsLearningPath[]>([]);
  const [state, setState] = useState<AdminLoadState>('loading');

  useEffect(() => {
    let isMounted = true;
    listAdminLearningPaths()
      .then((items) => {
        if (!isMounted) return;
        setPaths(items);
        setState('synced');
      })
      .catch(() => {
        if (!isMounted) return;
        setPaths([]);
        setState('local');
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const rows = paths.length
    ? paths.map((path) => [
        path.title,
        path.slug,
        path.description ?? 'No description',
        path.is_active === false ? 'Inactive' : 'Active',
      ])
    : [
        ['Surah Names', 'surah-names', 'Learn Quran chapter names, meanings, and short facts step by step.', 'Active'],
        ['Daily Duas', 'daily-duas', 'Practice short duas children can use during everyday routines.', 'Active'],
        ['Quran Meanings', 'quran-meanings', 'Build simple vocabulary and child-friendly Quran understanding.', 'Active'],
      ];

  function addOrReplacePath(nextPath: KidsLearningPath) {
    setPaths((current) => {
      const exists = current.some((path) => path.slug === nextPath.slug);
      return exists ? current.map((path) => (path.slug === nextPath.slug ? nextPath : path)) : [nextPath, ...current];
    });
  }

  return { addOrReplacePath, paths, rows, state };
}

export function useAdminLearningModuleRows() {
  const [modules, setModules] = useState<KidsLearningModule[]>([]);
  const [state, setState] = useState<AdminLoadState>('loading');

  useEffect(() => {
    let isMounted = true;
    listAdminLearningModules()
      .then((items) => {
        if (!isMounted) return;
        setModules(items);
        setState('synced');
      })
      .catch(() => {
        if (!isMounted) return;
        setModules([]);
        setState('local');
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const rows = modules.length
    ? modules.map((module) => [
        module.title,
        module.slug,
        module.status.replace(/_/g, ' '),
        module.description ?? 'No description',
      ])
    : [
        ['Surah Explorer', 'surah-explorer', 'active', 'Continue your journey through the Surahs.'],
        ['Prophets in the Quran', 'prophets-in-the-quran', 'active', 'Read Quran-based prophet lessons with guided quizzes.'],
      ];

  function addOrReplaceModule(nextModule: KidsLearningModule) {
    setModules((current) => {
      const exists = current.some((module) => module.slug === nextModule.slug);
      return exists ? current.map((module) => (module.slug === nextModule.slug ? nextModule : module)) : [nextModule, ...current];
    });
  }

  return { addOrReplaceModule, modules, rows, state };
}

export function useAdminLessonRows() {
  const [lessons, setLessons] = useState<KidsLesson[]>([]);
  const [state, setState] = useState<AdminLoadState>('loading');

  useEffect(() => {
    let isMounted = true;
    listAdminLessons()
      .then((items) => {
        if (!isMounted) return;
        setLessons(items);
        setState('synced');
      })
      .catch(() => {
        if (!isMounted) return;
        setLessons([]);
        setState('local');
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const rows = lessons.length
    ? lessons.map((lesson) => [
        lesson.title,
        lesson.slug,
        lesson.surah_id ? `Surah ${lesson.surah_id}` : 'No Surah',
        lesson.is_active ? 'Active' : 'Inactive',
      ])
    : [['Al-Fatihah Key Words', 'al-fatihah-key-words', 'Surah 1', 'Active']];

  function addOrReplaceLesson(nextLesson: KidsLesson) {
    setLessons((current) => {
      const exists = current.some((lesson) => lesson.slug === nextLesson.slug);
      return exists ? current.map((lesson) => (lesson.slug === nextLesson.slug ? nextLesson : lesson)) : [nextLesson, ...current];
    });
  }

  return { addOrReplaceLesson, lessons, rows, state };
}

export function useAdminAvatarRows() {
  const [avatars, setAvatars] = useState<KidsAvatar[]>([]);
  const [state, setState] = useState<AdminLoadState>('loading');

  useEffect(() => {
    let isMounted = true;
    listAdminAvatars()
      .then((items) => {
        if (!isMounted) return;
        setAvatars(items);
        setState('synced');
      })
      .catch(() => {
        if (!isMounted) return;
        setAvatars([]);
        setState('local');
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const rows = avatars.length
    ? avatars.map((avatar) => [
        avatar.title,
        avatar.slug,
        avatar.color_key ?? 'No color',
        avatar.is_active === false ? 'Inactive' : 'Active',
      ])
    : [['Ayah Reader', 'ayah-reader', 'emerald', 'Active']];

  function addOrReplaceAvatar(nextAvatar: KidsAvatar) {
    setAvatars((current) => {
      const exists = current.some((avatar) => avatar.slug === nextAvatar.slug);
      return exists ? current.map((avatar) => (avatar.slug === nextAvatar.slug ? nextAvatar : avatar)) : [nextAvatar, ...current];
    });
  }

  return { addOrReplaceAvatar, avatars, rows, state };
}

export function useAdminBadgeRows() {
  const [badges, setBadges] = useState<KidsBadge[]>([]);
  const [state, setState] = useState<AdminLoadState>('loading');

  useEffect(() => {
    let isMounted = true;
    listAdminBadges()
      .then((items) => {
        if (!isMounted) return;
        setBadges(items);
        setState('synced');
      })
      .catch(() => {
        if (!isMounted) return;
        setBadges([]);
        setState('local');
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const rows = badges.length
    ? badges.map((badge) => [
        badge.title,
        badge.slug,
        badge.description,
        badge.is_active === false ? 'Inactive' : 'Active',
      ])
    : rewardCards;

  function addOrReplaceBadge(nextBadge: KidsBadge) {
    setBadges((current) => {
      const exists = current.some((badge) => badge.slug === nextBadge.slug);
      return exists ? current.map((badge) => (badge.slug === nextBadge.slug ? nextBadge : badge)) : [nextBadge, ...current];
    });
  }

  return { badges, rows, state, addOrReplaceBadge };
}

export function useAdminRewardItemRows() {
  const [items, setItems] = useState<KidsRewardItem[]>([]);
  const [state, setState] = useState<AdminLoadState>('loading');

  useEffect(() => {
    let isMounted = true;
    listAdminRewardItems()
      .then((nextItems) => {
        if (!isMounted) return;
        setItems(nextItems);
        setState('synced');
      })
      .catch(() => {
        if (!isMounted) return;
        setItems([]);
        setState('local');
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const rows = items.length
    ? items.map((item) => [
        item.title,
        `${item.cost_stars} stars`,
        item.description ?? 'No description',
        item.is_active === false ? 'Inactive' : 'Active',
      ])
    : [
        ['Surah Revision Card', '100 stars', 'A printable card for reviewing key Surah facts.', 'Active'],
        ['Morning Dua Checklist', '75 stars', 'A checklist to help children revise their daily duas.', 'Active'],
      ];

  function addOrReplaceRewardItem(nextItem: KidsRewardItem) {
    setItems((current) => {
      const exists = current.some((item) => item.slug === nextItem.slug);
      return exists ? current.map((item) => (item.slug === nextItem.slug ? nextItem : item)) : [nextItem, ...current];
    });
  }

  return { addOrReplaceRewardItem, items, rows, state };
}

export function useAdminFeatureFlagRows() {
  const [flags, setFlags] = useState<KidsFeatureFlag[]>([]);
  const [state, setState] = useState<AdminLoadState>('loading');

  useEffect(() => {
    let isMounted = true;
    listAdminFeatureFlags()
      .then((items) => {
        if (!isMounted) return;
        setFlags(items);
        setState('synced');
      })
      .catch(() => {
        if (!isMounted) return;
        setFlags([]);
        setState('local');
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const rows = flags.length
    ? flags.map((flag) => [
        flag.title,
        flag.slug,
        flag.status.replace(/_/g, ' '),
        flag.description ?? 'No description',
      ])
    : [
        ['Voice Analysis', 'voice-analysis', 'coming soon', 'Intelligent feedback to help children improve recitation.'],
        ['Quiz Revision Mode', 'quiz-revision-mode', 'coming soon', 'Extra review prompts after each Quran quiz session.'],
      ];

  function addOrReplaceFlag(nextFlag: KidsFeatureFlag) {
    setFlags((current) => {
      const exists = current.some((flag) => flag.slug === nextFlag.slug);
      return exists ? current.map((flag) => (flag.slug === nextFlag.slug ? nextFlag : flag)) : [nextFlag, ...current];
    });
  }

  return { addOrReplaceFlag, flags, rows, state };
}

export function useAdminQuizRows() {
  const [rows, setRows] = useState<string[][]>(adminQuizRows);
  const [readiness, setReadiness] = useState<AdminQuizReadinessRow[]>([]);
  const [state, setState] = useState<AdminLoadState>('loading');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let isMounted = true;
    listAdminQuizReadiness()
      .then((items) => {
        if (!isMounted) return;
        setReadiness(items);
        setRows(
          items.length
            ? items.map((quiz) => [
                quiz.quiz_title,
                quiz.category_title,
                `${quiz.actual_questions}/${quiz.declared_questions} questions`,
                quiz.publish_status ?? 'draft',
                quiz.status,
              ])
            : adminQuizRows,
        );
        setState('synced');
      })
      .catch(() => {
        if (!isMounted) return;
        setReadiness([]);
        setRows(adminQuizRows);
        setState('local');
      });

    return () => {
      isMounted = false;
    };
  }, [refreshKey]);

  function refresh() {
    setRefreshKey((current) => current + 1);
  }

  return { readiness, rows, state, refresh };
}

export function useAdminArticleRows() {
  const [articles, setArticles] = useState<KidsLearningArticle[]>([]);
  const [rows, setRows] = useState<string[][]>(articleRows);
  const [state, setState] = useState<AdminLoadState>('loading');

  useEffect(() => {
    let isMounted = true;
    listAdminArticles()
      .then((articles) => {
        if (!isMounted) return;
        setArticles(articles);
        setRows(
          articles.length
            ? articles.map((article) => [
                article.title,
                article.slug,
                article.status,
                article.related_quiz_id ? 'Related quiz linked' : article.summary ?? 'No summary yet',
              ])
            : articleRows,
        );
        setState('synced');
      })
      .catch(() => {
        if (!isMounted) return;
        setArticles([]);
        setRows(articleRows);
        setState('local');
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return { articles, rows, state };
}

export function useAdminModerationRows() {
  const [requests, setRequests] = useState<KidsParentRequest[]>([]);
  const [state, setState] = useState<AdminLoadState>('loading');

  useEffect(() => {
    let isMounted = true;
    listAdminParentRequests()
      .then((items) => {
        if (!isMounted) return;
        setRequests(items);
        setState('synced');
      })
      .catch(() => {
        if (!isMounted) return;
        setRequests([]);
        setState('local');
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const rows = requests.length
    ? requests.map((request) => [
        request.parent_name,
        request.message.length > 52 ? `${request.message.slice(0, 52)}...` : request.message,
        request.request_type.replace(/_/g, ' '),
        request.status,
      ])
    : moderationRows;

  function replaceRequest(nextRequest: KidsParentRequest) {
    setRequests((current) => current.map((request) => (request.id === nextRequest.id ? nextRequest : request)));
  }

  return { requests, rows, state, replaceRequest };
}

export function useAdminMediaRows() {
  const [items, setItems] = useState<KidsMediaAsset[]>([]);
  const [state, setState] = useState<AdminLoadState>('loading');

  useEffect(() => {
    let isMounted = true;
    listMediaAssets()
      .then((assets) => {
        if (!isMounted) return;
        setItems(assets);
        setState('synced');
      })
      .catch(() => {
        if (!isMounted) return;
        setItems([]);
        setState('local');
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return { items, state };
}

export function useAdminAuditRows() {
  const [entries, setEntries] = useState<KidsCmsAuditLog[]>([]);
  const [state, setState] = useState<AdminLoadState>('loading');

  useEffect(() => {
    let isMounted = true;
    listAdminAuditLog()
      .then((items) => {
        if (!isMounted) return;
        setEntries(items);
        setState('synced');
      })
      .catch(() => {
        if (!isMounted) return;
        setEntries([]);
        setState('local');
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const rows = entries.map((entry) => [
    formatDateTime(entry.created_at),
    entry.action,
    entry.entity_type.replace(/^kids_/, '').replace(/_/g, ' '),
    getAuditSummary(entry),
  ]);

  return { entries, rows, state };
}
